import React, { useRef } from 'react';
import ReactApexChart from 'react-apexcharts';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import DownloadIcon from '@mui/icons-material/Download';
import { AiChatChartData } from '../../../core/modules/aiChat/services/api';

export interface AiChatMessageData {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  isError?: boolean;
  isPending?: boolean;
  columns?: string[];
  rows?: unknown[][];
  sql?: string | null;
  chart?: AiChatChartData | null;
}

// Filas visibles en la tabla del chat antes de recortar -- las 200 que puede
// devolver el backend (ver ai_chat_sql_validator.py) no caben comodas en un
// panel de chat angosto, esto es solo una vista previa legible.
const MAX_TABLE_ROWS_SHOWN = 25;

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') return value.toLocaleString('es-CL');
  return String(value);
}

// CSV pensado para reabrirse en Excel/Sheets -- valores crudos (sin el
// formato "es-CL" que usa la tabla en pantalla, que confundiria una
// herramienta de analisis) y con comillas/BOM para que los acentos y las
// comas dentro de un valor no rompan el archivo.
function escapeCsvField(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function buildCsv(columns: string[], rows: unknown[][]): string {
  const headerLine = columns.map(escapeCsvField).join(',');
  const rowLines = rows.map((row) => row.map(escapeCsvField).join(','));
  return [headerLine, ...rowLines].join('\r\n');
}

function downloadResultsAsCsv(columns: string[], rows: unknown[][]): void {
  const csvContent = buildCsv(columns, rows);
  // \uFEFF: BOM -- sin esto Excel a veces interpreta mal los acentos (UTF-8 sin BOM).
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  link.href = url;
  link.download = `chatbot-datos-${timestamp}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// El grafico se renderiza con fondo transparente (ver 'background: transparent'
// mas abajo) -- pero JPG no soporta transparencia, asi que antes de exportar
// se dibuja sobre un canvas con fondo blanco (si no, el visor de imagenes le
// pinta negro por default a lo transparente, ilegible con texto oscuro).
// chart.dataURI() es la API publica de ApexCharts (instancia expuesta como
// `.chart` por react-apexcharts en el ref del componente), devuelve un PNG
// en base64 -- se re-codifica a JPG via canvas antes de descargar.
function downloadChartAsJpg(chartInstance: any): void {
  if (!chartInstance || typeof chartInstance.dataURI !== 'function') return;
  chartInstance.dataURI().then(({ imgURI }: { imgURI: string }) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      const jpgDataUrl = canvas.toDataURL('image/jpeg', 0.92);
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      link.href = jpgDataUrl;
      link.download = `chatbot-grafico-${timestamp}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
    img.src = imgURI;
  });
}

function AiChatResultChart({ chart }: { chart: AiChatChartData }) {
  const theme = useTheme();
  const chartComponentRef = useRef<any>(null);

  const options: any = {
    chart: {
      fontFamily: 'inherit',
      foreColor: 'inherit',
      toolbar: { show: false },
      zoom: { enabled: false },
      background: 'transparent',
    },
    xaxis: {
      categories: chart.categories,
      labels: { style: { fontSize: '11px' } },
    },
    yaxis: {
      labels: {
        style: { fontSize: '11px' },
        formatter: (val: number) => val.toLocaleString('es-CL'),
      },
    },
    dataLabels: { enabled: false },
    colors: [theme.palette.primary.main],
    grid: { borderColor: theme.palette.divider },
    // OJO: nunca pasar 'undefined' aca -- apexcharts hace un merge de esto
    // sobre su config por defecto, y un valor 'undefined' explicito (a
    // diferencia de simplemente omitir la clave) borra el objeto stroke por
    // defecto en vez de respetarlo, dejando config.stroke literalmente
    // undefined. Un modulo interno de la libreria despues lee
    // config.stroke.colors sin chequear -- eso revienta con
    // "Cannot read properties of undefined (reading 'colors')" apenas se
    // muestra un grafico de barras (bug real encontrado en produccion).
    stroke:
      chart.chartType === 'line'
        ? { curve: 'smooth', width: 3 }
        : { width: 0 },
  };

  const series = [{ name: 'Valor', data: chart.series }];

  return (
    <Box sx={{ mt: 1, mx: -1 }}>
      <ReactApexChart ref={chartComponentRef} options={options} series={series} type={chart.chartType} height={220} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5, mx: 1 }}>
        <Button
          size="small"
          variant="text"
          startIcon={<DownloadIcon fontSize="small" />}
          onClick={() => downloadChartAsJpg(chartComponentRef.current?.chart)}
          sx={{ fontSize: '0.75rem', textTransform: 'none' }}
        >
          Descargar JPG
        </Button>
      </Box>
    </Box>
  );
}

function AiChatResultTable({ columns, rows }: { columns: string[]; rows: unknown[][] }) {
  const visibleRows = rows.slice(0, MAX_TABLE_ROWS_SHOWN);

  return (
    <Box sx={{ mt: 1 }}>
      <TableContainer sx={{ maxHeight: 260, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col} sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, rowIdx) => (
              <TableRow key={rowIdx} hover>
                {row.map((cell, cellIdx) => (
                  <TableCell key={cellIdx} sx={{ fontSize: '0.75rem' }}>
                    {formatCellValue(cell)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length > MAX_TABLE_ROWS_SHOWN && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5, gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Mostrando {MAX_TABLE_ROWS_SHOWN} de {rows.length} filas.
          </Typography>
          <Button
            size="small"
            variant="text"
            startIcon={<DownloadIcon fontSize="small" />}
            onClick={() => downloadResultsAsCsv(columns, rows)}
            sx={{ fontSize: '0.75rem', textTransform: 'none', flexShrink: 0 }}
          >
            Descargar CSV ({rows.length})
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default function AiChatMessage({ message }: { message: AiChatMessageData }) {
  const isUser = message.role === 'user';

  if (message.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
        <Paper
          variant="outlined"
          sx={{ px: 1.5, py: 1, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <CircularProgress size={14} />
          <Typography variant="body2" color="text.secondary">
            Pensando...
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', mb: 1.5 }}>
      <Paper
        variant={isUser ? 'elevation' : 'outlined'}
        elevation={isUser ? 2 : 0}
        sx={{
          px: 1.5,
          py: 1,
          borderRadius: 2,
          maxWidth: '88%',
          bgcolor: (theme) =>
            isUser
              ? theme.palette.primary.main
              : message.isError
              ? alpha(theme.palette.error.main, 0.08)
              : theme.palette.background.paper,
          color: isUser ? 'primary.contrastText' : 'text.primary',
          borderColor: message.isError ? 'error.main' : undefined,
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {message.text}
        </Typography>

        {!isUser && message.columns && message.rows && message.rows.length > 0 && (
          <AiChatResultTable columns={message.columns} rows={message.rows} />
        )}

        {!isUser && message.chart && <AiChatResultChart chart={message.chart} />}
      </Paper>
    </Box>
  );
}
